<template>
  <div class="position-relative">
    <loader color="grey" v-if="loading"/>

    <b-row class="mb-3">
      <b-col class="col-12 col-md-6 mb-3 mb-sm-0">
        <h2 class="mb-0 text-uppercase">{{ $t('billing.invoices') }}</h2>
      </b-col>
      <b-col class="col-6 col-sm-6 col-md-3">
        <multiselect
            v-model="selectedPeriod"
            label="title"
            track-by="title"
            :placeholder="$t('billing.select_period')"
            :options="periodsOptions"
            :searchable="false"
            :close-on-select="true"
            selectLabel=""
            selectedLabel=""
        />

      </b-col>
      <b-col class="col-6 col-sm-6 col-md-3">
        <multiselect
            v-model="selectedResource"
            label="title"
            track-by="title"
            :placeholder="$t('billing.select_resource')"
            :options="resourcesOptions"
            :searchable="false"
            :close-on-select="true"
            selectLabel=""
            selectedLabel="" />
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col v-if="isRoot">
        <div class="d-flex align-items-center h-100">
          <b-btn @click="showGenerateInvoiceModal = true">{{ $t('billing.generate') }}</b-btn>
        </div>
      </b-col>
      <b-col>
        <div class="h-100 d-flex align-items-center justify-content-end">
          <formFieldSwitch id="livemode"
                           :label="$t('resources.live_mode')"
                           v-model="livemode" />
        </div>
      </b-col>
    </b-row>

    <div class="invoices-wrapper" style="min-height: 250px">
      <div v-for="(item, index) in invoices"
           :key="index">
        <div class="white-container">
          <InvoicesInfo
              :total="item.total"
              :status="item.status"
              :label="item.label"
              :isPaid="item.isPaid"
              :is-finalised="item.isFinalised"
              :invoice-pdf-url="item.invoicePdf"
              :invoice-number="item.invoiceNumber"
              :items="item.items"
              :id="item.id"
              :resource="item.resource"
              :payment-due-date="item.paymentDueDate"
              :payment-ability="false"
              :period="item.period"
              @finalized="fetchInvoices(currentPage)"
              @resource-select="onResourceSelect"
          />
        </div>
      </div>

      <b-pagination v-if="totalItems > 0"
                    v-model="currentPage"
                    :total-rows="totalItems"
                    :per-page="limit" />
    </div>

    <ModalGenerateInvoice v-model="showGenerateInvoiceModal"
                          :resources="resourcesOptions" />
  </div>
</template>

<script>
import InvoicesInfo from '../billing/InvoicesInfo';
import moment from "moment";
import InvoicesMixin from "../../mixins/invoices";
import formFieldSwitch from '../../components/forms/fields/formFieldSwitch';
import ModalGenerateInvoice from "../../components/modal/ModalGenerateInvoice";

export default {
  name: "InvoicesInfoView",
  components: {
    InvoicesInfo,
    formFieldSwitch,
    ModalGenerateInvoice
  },
  mixins: [InvoicesMixin],
  data() {
    return {
      livemode: true,
      loading: true,
      totalItems: 0,
      limit: 5,
      currentPage: 1,
      invoices: [],
      selectedPeriod: null,
      periodsOptions: [],
      resourcesOptions: [],
      selectedResource: null,
      showGenerateInvoiceModal: false
    }
  },
  created () {
    this.getAllData();
  },
  computed: {
    selectedPeriodStart () {
      return this.selectedPeriod ? moment(this.selectedPeriod.title).format('YYYY-MM') : null;
    },
    selectedResourceId () {
      return this.selectedResource ? this.selectedResource.id : null;
    },
    isRoot () {
      return this.$store.getters['auth/role'] === "root";
    }
  },
  methods: {
    getAllData () {
      this.loading = true;
      Promise.allSettled([this.fetchPeriods(), this.getResources(), this.fetchInvoices()])
          .finally(() => this.loading = false);
    },
    getResources () {
      return this.getData(`pricing/invoices/resources`, {
        params: {
          calendar_period: this.selectedPeriodStart,
          livemode: this.livemode
        }
      }, false)
          .then(({ data }) => {
            if (Array.isArray(data.items)) {
              this.resourcesOptions = data.items;
            }
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          });
    },
    fetchPeriods () {
      return this.getData(`resources/pricing/invoices/periods`, {
        params: {
          resource_id: this.selectedResourceId,
          livemode: this.livemode
        }
      }, false)
          .then(({ data }) => {
            if (Array.isArray(data.items)) {
              this.periodsOptions = data.items.map((dateStr) => {
                return {
                  title: this.formatPeriodTitle(dateStr)
                }
              });

              return this.periodsOptions;
            }
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          });
    },
    fetchInvoices (page = 1) {
      return this.getData(`resources/pricing/invoices`, {
        params: {
          limit: this.limit,
          page: page,
          calendar_period: this.selectedPeriodStart,
          resource_id: this.selectedResourceId,
          livemode: this.livemode
        }
      }, false)
          .then(({ data }) => {
            if (data.items) {
              if (data.paginator) {
                this.totalItems = data.paginator.total_items;
              }

              this.invoices = data.items.map((item) => this.formatInvoiceObject(item));
            }
          }).catch((error) => {
            if (error && error.response) {
              this.$noty.error(error.response.data.error_description);
            }
          });
    },
    onResourceSelect (resource) {
      if (resource) {
        this.selectedResource = resource;
      }
    }
  },
  watch: {
    livemode: function() {
      this.getAllData();
    },
    selectedPeriod: function () {
      this.getAllData();
    },
    selectedResource: function () {
      this.getAllData();
    },
    currentPage: function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loading = true;
        this.fetchInvoices(newVal)
            .finally(() => {
              this.loading = false
            });
      }
    }
  }
}
</script>